import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    Plus, Sparkles, Trash2, Eye, EyeOff, Pencil, ExternalLink, Loader2,
} from "lucide-react";
import { api } from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";

const STATUS_STYLE = {
    draft: "border-[var(--color-border)] text-text-secondary",
    review: "border-[var(--color-secondary)]/40 bg-[var(--color-secondary)]/10 text-[var(--color-secondary)]",
    published: "border-[var(--color-success)]/40 bg-[var(--color-success)]/10 text-[var(--color-success)]",
    archived: "border-[var(--color-alert)]/40 bg-[var(--color-alert)]/10 text-[var(--color-alert)]",
};

export default function AdminBlog() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [categories, setCategories] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");
    const [loading, setLoading] = useState(true);

    const [collapsed, setCollapsed] = useState(false);

    // AI Draft form
    const [aiTopic, setAiTopic] = useState("");
    const [aiCategory, setAiCategory] = useState("product-updates");
    const [aiBusy, setAiBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [postsResp, catsResp] = await Promise.all([
                api.adminBlogList({ status: statusFilter || undefined, limit: 100 }),
                api.blogCategories(),
            ]);
            setItems(postsResp.items || []);
            setCounts(postsResp.counts || {});
            setCategories(catsResp.items || []);
        } catch (e) {
            toast.error(`Failed to load: ${e.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    const handleAiDraft = async () => {
        if (aiTopic.trim().length < 8) {
            toast.error("Topic must be at least 8 characters");
            return;
        }
        setAiBusy(true);
        try {
            const resp = await api.adminBlogAiDraft(aiTopic.trim(), aiCategory);
            toast.success("Draft created — please review before publish");
            setAiTopic("");
            navigate(`/admin/blog/${resp.post.id}`);
        } catch (e) {
            toast.error(`AI draft failed: ${e.message}`);
        } finally {
            setAiBusy(false);
        }
    };

    const publish = async (id) => {
        if (!window.confirm("Publish this post? It will be publicly visible.")) return;
        try {
            await api.adminBlogPublish(id);
            toast.success("Published");
            load();
        } catch (e) {
            toast.error(e.message);
        }
    };
    const unpublish = async (id) => {
        try {
            await api.adminBlogUnpublish(id);
            toast.success("Moved to draft");
            load();
        } catch (e) { toast.error(e.message); }
    };
    const remove = async (id) => {
        if (!window.confirm("Delete post permanently?")) return;
        try {
            await api.adminBlogDelete(id);
            toast.success("Deleted");
            load();
        } catch (e) { toast.error(e.message); }
    };
    const newBlank = async () => {
        try {
            const resp = await api.adminBlogCreate({
                title: "Untitled draft",
                content_markdown: "Write your post here.",
                category_slug: aiCategory,
            });
            navigate(`/admin/blog/${resp.post.id}`);
        } catch (e) { toast.error(e.message); }
    };

    return (
        <div className="flex min-h-screen bg-[var(--color-bg)] text-text-main">
            <Sidebar collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />
            <div className="flex-1 flex flex-col min-w-0">
                <Header />
                <main className="p-6 sm:p-10 max-w-6xl w-full">
                    <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
                        <div>
                            <p className="text-[10px] uppercase tracking-widest text-text-secondary">Phase 6A</p>
                            <h1 className="text-3xl font-semibold tracking-tight mt-1">Blog CMS</h1>
                            <p className="text-sm text-text-secondary mt-1">
                                AI-drafted posts always require human review before publish.
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <Link
                                to="/blog"
                                target="_blank"
                                rel="noreferrer"
                                data-testid="admin-blog-view-public"
                                className="inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-2 text-sm text-text-secondary hover:text-text-main transition-colors"
                            >
                                <ExternalLink className="h-3.5 w-3.5" />
                                View public
                            </Link>
                            <button
                                data-testid="admin-blog-new"
                                onClick={newBlank}
                                className="inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-2 text-sm hover:border-[var(--color-primary)]/50 transition-colors"
                            >
                                <Plus className="h-3.5 w-3.5" />
                                New post
                            </button>
                        </div>
                    </div>

                    {/* AI Draft panel */}
                    <motion.section
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 mb-8"
                    >
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles className="h-4 w-4 text-[var(--color-secondary)]" />
                            <h2 className="text-sm font-semibold uppercase tracking-widest">AI Draft</h2>
                        </div>
                        <p className="text-xs text-text-secondary mb-4">
                            Generate a full markdown draft from a topic. You must review and edit before publish (enforced server-side).
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3">
                            <input
                                data-testid="admin-blog-ai-topic"
                                value={aiTopic}
                                onChange={(e) => setAiTopic(e.target.value)}
                                placeholder="Topic (e.g. 'How to build a repeatable outbound engine in 2026')"
                                className="flex-1 rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] px-4 py-2.5 text-sm outline-none focus:border-[var(--color-primary)]/60"
                            />
                            <select
                                data-testid="admin-blog-ai-category"
                                value={aiCategory}
                                onChange={(e) => setAiCategory(e.target.value)}
                                className="rounded-xl border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-2.5 text-sm outline-none focus:border-[var(--color-primary)]/60"
                            >
                                {categories.map((c) => (
                                    <option key={c.slug} value={c.slug}>{c.name}</option>
                                ))}
                            </select>
                            <button
                                data-testid="admin-blog-ai-generate"
                                onClick={handleAiDraft}
                                disabled={aiBusy}
                                className="inline-flex items-center gap-2 rounded-xl gradient-bg-cta px-5 py-2.5 text-sm font-medium text-white glow-primary disabled:opacity-60"
                            >
                                {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                                Generate draft
                            </button>
                        </div>
                    </motion.section>

                    {/* Status filters */}
                    <div className="flex flex-wrap gap-2 mb-6">
                        {[
                            { k: "", label: `All (${(counts.draft || 0) + (counts.review || 0) + (counts.published || 0) + (counts.archived || 0)})` },
                            { k: "draft", label: `Drafts (${counts.draft || 0})` },
                            { k: "published", label: `Published (${counts.published || 0})` },
                            { k: "archived", label: `Archived (${counts.archived || 0})` },
                        ].map((s) => (
                            <button
                                key={s.k || "all"}
                                data-testid={`admin-blog-filter-${s.k || "all"}`}
                                onClick={() => setStatusFilter(s.k)}
                                className={`rounded-full border px-3 py-1.5 text-xs uppercase tracking-widest transition-colors ${
                                    statusFilter === s.k
                                        ? "border-[var(--color-primary)] bg-[var(--color-primary)]/15 text-[var(--color-primary)]"
                                        : "border-[var(--color-border)] text-text-secondary hover:text-text-main"
                                }`}
                            >
                                {s.label}
                            </button>
                        ))}
                    </div>

                    {/* Posts table */}
                    {loading ? (
                        <div className="text-text-secondary">…</div>
                    ) : items.length === 0 ? (
                        <div
                            data-testid="admin-blog-empty"
                            className="rounded-2xl border border-dashed border-[var(--color-border)] p-16 text-center text-text-secondary"
                        >
                            No posts yet.
                        </div>
                    ) : (
                        <div
                            data-testid="admin-blog-list"
                            className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] overflow-hidden"
                        >
                            <table className="w-full text-sm">
                                <thead className="text-[10px] uppercase tracking-widest text-text-secondary bg-[var(--color-panel-ai)]">
                                    <tr>
                                        <th className="text-left px-5 py-3">Title</th>
                                        <th className="text-left px-3 py-3">Category</th>
                                        <th className="text-left px-3 py-3">Status</th>
                                        <th className="text-left px-3 py-3">Author</th>
                                        <th className="text-right px-5 py-3">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((p) => (
                                        <tr
                                            key={p.id}
                                            data-testid={`admin-blog-row-${p.slug}`}
                                            className="border-t border-[var(--color-border)] hover:bg-[var(--color-panel-ai)]/40 transition-colors"
                                        >
                                            <td className="px-5 py-3">
                                                <div className="flex items-center gap-2">
                                                    <Link
                                                        to={`/admin/blog/${p.id}`}
                                                        className="font-medium hover:text-[var(--color-primary)]"
                                                    >
                                                        {p.title}
                                                    </Link>
                                                    {p.ai_generated && (
                                                        <span
                                                            title="AI-generated"
                                                            className="rounded border border-[var(--color-secondary)]/40 bg-[var(--color-secondary)]/10 text-[var(--color-secondary)] px-1.5 py-0.5 text-[9px] uppercase tracking-widest"
                                                        >
                                                            AI
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-text-secondary mt-0.5">{p.slug}</div>
                                            </td>
                                            <td className="px-3 py-3 text-text-secondary text-xs">{p.category_slug}</td>
                                            <td className="px-3 py-3">
                                                <span
                                                    className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${STATUS_STYLE[p.status] || STATUS_STYLE.draft}`}
                                                >
                                                    {p.status}
                                                </span>
                                            </td>
                                            <td className="px-3 py-3 text-text-secondary text-xs">{p.author_name}</td>
                                            <td className="px-5 py-3">
                                                <div className="flex items-center gap-1 justify-end">
                                                    <Link
                                                        to={`/admin/blog/${p.id}`}
                                                        data-testid={`admin-blog-edit-${p.slug}`}
                                                        className="p-1.5 rounded hover:bg-[var(--color-panel-ai)] text-text-secondary hover:text-text-main"
                                                        title="Edit"
                                                    >
                                                        <Pencil className="h-3.5 w-3.5" />
                                                    </Link>
                                                    {p.status === "published" ? (
                                                        <button
                                                            data-testid={`admin-blog-unpublish-${p.slug}`}
                                                            onClick={() => unpublish(p.id)}
                                                            className="p-1.5 rounded hover:bg-[var(--color-panel-ai)] text-text-secondary hover:text-text-main"
                                                            title="Unpublish"
                                                        >
                                                            <EyeOff className="h-3.5 w-3.5" />
                                                        </button>
                                                    ) : (
                                                        <button
                                                            data-testid={`admin-blog-publish-${p.slug}`}
                                                            onClick={() => publish(p.id)}
                                                            className="p-1.5 rounded hover:bg-[var(--color-panel-ai)] text-[var(--color-success)]"
                                                            title="Publish"
                                                        >
                                                            <Eye className="h-3.5 w-3.5" />
                                                        </button>
                                                    )}
                                                    <button
                                                        data-testid={`admin-blog-delete-${p.slug}`}
                                                        onClick={() => remove(p.id)}
                                                        className="p-1.5 rounded hover:bg-[var(--color-alert)]/10 text-[var(--color-alert)]"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="h-3.5 w-3.5" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
